/**
 * Button Component
 *
 *   - `tone`: the fill of a tone (`TONE_CLASSES`) instead of the brand's — a
 *     hero's actions wear its own colour (`tone={HERO_TONE.inout}`), its ink
 *     and its hover a shade away from the ink (tones.spec.ts). Only for the
 *     default (filled) variant.
 *   - `loading`: the action is on its way — a spinner beside the words (in
 *     place of the button's own leading icon), `aria-busy`, and no second
 *     click (disabled). The words stay, so the button keeps its size and its
 *     name.
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@tonnie/foundation';
import type { Tone } from '@tonnie/design-tokens';
import { Loader2Icon } from 'lucide-react';

import { TONE_CLASSES } from '../lib/tone-classes';
import type { IconButtonSize, NamedBySize } from './control-name';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&>[data-slot=button-spinner]+svg]:hidden',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        success:
          'bg-tone-green-solid text-tone-green-on-solid shadow-sm hover:bg-tone-green-solid-hover',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-border bg-background shadow-sm hover:bg-muted hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-muted hover:text-accent-foreground',
        link: 'text-primary-accessible underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-6',
        icon: 'h-9 w-9',
        'icon-sm': 'h-8 w-8',
        'icon-lg': 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

interface ButtonOwnProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** A tone's fill instead of the brand's — the default (filled) variant only. */
  tone?: Tone;
  /** The action is on its way: a spinner, `aria-busy`, no second click (not with `asChild`). */
  loading?: boolean;
}

/** An icon-only button (`size="icon*"`) must be named — it does not compile otherwise. */
export type ButtonProps = NamedBySize<ButtonOwnProps>;

/** A button that always shows its words (never an icon size) — for wrappers that render a label. */
export type TextButtonProps = ButtonOwnProps & {
  size?: Exclude<ButtonOwnProps['size'], IconButtonSize>;
};

/**
 * The classes of a tone's filled button — its fill, its ink, its hover. For a
 * control styled as a button that is not one (an `AlertDialogAction`).
 */
export function buttonToneClasses(tone: Tone): string {
  const classes = TONE_CLASSES[tone];
  return cn(classes.solid, classes.onSolid, classes.solidHover, 'shadow-sm');
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      tone,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    // A tone fills the default variant only: an outline or a link keeps its own look.
    const filled = (variant ?? 'default') === 'default';
    const classes = cn(
      buttonVariants({ variant, size }),
      tone && filled && buttonToneClasses(tone),
      className
    );
    if (asChild) {
      return (
        <Slot className={classes} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        className={classes}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <Loader2Icon
            data-slot="button-spinner"
            className="size-4 animate-spin"
            aria-hidden="true"
          />
        ) : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
